/**
 * The packages GoSpeedNet actually sells, and the company's own particulars.
 *
 * Taken from www.gospeednet.co.ke, and that is the point of the file: the
 * price on a contract, the price a salesperson quotes and the price on the
 * website have to be the same number. When they live in three places they
 * diverge, and the one that ends up wrong is always the one with a customer's
 * signature under it.
 *
 * **When prices change on the website, change them here in the same hour.**
 * Contracts already signed are unaffected — a signed contract carries its own
 * frozen copy of every figure, so editing this file cannot alter what anybody
 * agreed to. It only changes what the *next* draft is pre-filled with.
 */
/**
 * Home fibre, exactly as published.
 *
 * Every one of these is unlimited data with free standard installation, which
 * is why there is no cap field: an allowance nobody enforces is a promise
 * waiting to be broken, and this business does not sell one.
 */
export const HOME_PLANS = [
    {
        code: 'HOME5',
        name: 'Home 5',
        tier: 'home',
        speedMbps: 5,
        monthlyFeeKes: 999,
        installFeeKes: 0,
        suits: 'One or two people. Browsing, WhatsApp, music, the occasional stream.',
    },
    {
        code: 'HOME10',
        name: 'Home 10',
        tier: 'home',
        speedMbps: 10,
        monthlyFeeKes: 1_499,
        installFeeKes: 0,
        suits: '1–2 people. Browsing, social media, one HD stream.',
    },
    {
        code: 'HOME15',
        name: 'Home 15',
        tier: 'home',
        speedMbps: 15,
        monthlyFeeKes: 1_999,
        installFeeKes: 0,
        suits: '2–3 people. HD streaming plus light work from home.',
    },
    {
        code: 'HOME20',
        name: 'Home 20',
        tier: 'home',
        speedMbps: 20,
        monthlyFeeKes: 2_499,
        installFeeKes: 0,
        suits: '3–4 people. Video calls and streaming at the same time.',
    },
    {
        code: 'HOME30',
        name: 'Home 30',
        tier: 'home',
        speedMbps: 30,
        monthlyFeeKes: 2_999,
        installFeeKes: 0,
        suits: '4–5 people, or gaming alongside streaming.',
    },
    {
        code: 'HOME50',
        name: 'Home 50',
        tier: 'home',
        speedMbps: 50,
        monthlyFeeKes: 4_500,
        installFeeKes: 0,
        suits: 'Busy family home, several screens, some 4K.',
    },
    {
        code: 'HOME70',
        name: 'Home 70',
        tier: 'home',
        speedMbps: 70,
        monthlyFeeKes: 5_000,
        installFeeKes: 0,
        suits: 'Heavy use, multiple people working from home.',
    },
    {
        code: 'HOME100',
        name: 'Home 100',
        tier: 'home',
        speedMbps: 100,
        monthlyFeeKes: 6_000,
        installFeeKes: 0,
        suits: 'Large household, 4K on several screens, big downloads.',
    },
];
/**
 * Business fibre.
 *
 * The website names 50 Mbps and 100 Mbps for SMEs, offices and schools without
 * publishing a price — business connections are quoted, because the cost is
 * driven by the build to the premises rather than by the speed. The monthly
 * figures here are therefore a **starting point for a quote**, deliberately set
 * at the home price for the same speed, and every business contract is expected
 * to have them edited before it is sent.
 */
export const BUSINESS_PLANS = [
    {
        code: 'BIZ50',
        name: 'Business 50',
        tier: 'business',
        speedMbps: 50,
        monthlyFeeKes: 4_500,
        installFeeKes: 0,
        suits: 'Small office, shop or clinic. Quoted per site.',
    },
    {
        code: 'BIZ100',
        name: 'Business 100',
        tier: 'business',
        speedMbps: 100,
        monthlyFeeKes: 6_000,
        installFeeKes: 0,
        suits: 'Offices, schools and growing organisations. Quoted per site.',
    },
];
export const PLANS = [...HOME_PLANS, ...BUSINESS_PLANS];
export function planByCode(code) {
    const wanted = code.trim().toUpperCase();
    return PLANS.find((plan) => plan.code === wanted) ?? null;
}
export function plansFor(tier) {
    return tier === 'home' ? HOME_PLANS : BUSINESS_PLANS;
}
/**
 * The cheapest plan that comfortably carries this many simultaneous heavy
 * users — the recommendation a salesperson would otherwise make by feel.
 *
 * Roughly 8 Mbps a head, which is one HD stream or one video call with room
 * to spare, floored at the entry plan. Advice, not a rule: the caller is free
 * to sell something else, and usually should when the customer says "4K".
 */
export function suggestPlan(heavyUsers, tier = 'home') {
    const needed = Math.max(1, Math.ceil(heavyUsers)) * 8;
    const candidates = plansFor(tier);
    return candidates.find((plan) => plan.speedMbps >= needed) ?? candidates[candidates.length - 1];
}
/** KES, grouped the way it is written on an invoice in Kenya. */
export function formatKes(amount) {
    return `KES ${Math.round(amount).toLocaleString('en-KE')}`;
}
/**
 * The company's own particulars, as published.
 *
 * These end up printed on every contract, so they live beside the prices
 * rather than being typed into a template by hand each time.
 */
export const COMPANY = {
    legalName: 'GoSpeedNet Ltd',
    tradingName: 'GoSpeedNet',
    town: 'Kisii',
    county: 'Kisii County',
    country: 'Kenya',
    phoneE164: '+254742055339',
    email: 'gospeednet1@gmail.com',
    website: 'https://www.gospeednet.co.ke',
    /** Where the published terms live. Referenced by every contract body. */
    termsUrl: 'https://www.gospeednet.co.ke/terms-conditions',
    /*
     * Postal and registry particulars, printed on the cover of every agreement.
     *
     * Empty until somebody who knows them fills them in, and every one of them
     * is rendered only when set — see `DocumentCover`. That is deliberate and
     * not a stub: a P.O. Box, a company number and a KRA PIN are exactly the
     * fields that make a cover page look like a real company's, and exactly the
     * fields that must never be invented. A blank line is obviously missing; a
     * plausible wrong one is quietly false on a document somebody signs.
     *
     * Filling one in is a one-word change here and it appears on every cover,
     * every letterhead and every printed copy at once.
     */
    /** The box number alone — "1234". The word "P.O. Box" is added when printed. */
    poBox: '',
    /** The postal code that goes with it — Kisii is 40200. */
    postalCode: '',
    /** As registered with the Registrar of Companies — "PVT-ABCDEF". */
    registrationNo: '',
    /** The company's own KRA PIN — "P051234567X". */
    kraPin: '',
};
//# sourceMappingURL=plans.js.map